import {
  Component,
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import { Slots } from '../contexts/Slots.jsx';
import type { SlotName } from '../SlotsManager.js';

export interface SlotProps {
  name: SlotName;
  onRender?: (slotValue: ReactNode) => void;
}

type SlotErrorBoundaryState = {
  hasError: boolean;
  prevChildren: ReactNode;
};

/**
 * Lightweight error boundary that catches render errors in slot content
 * and renders null instead of crashing. Auto-resets when children change
 * (i.e., when new slot content arrives after the old one errored).
 *
 * This handles the timing gap during route transitions where stale sidebar
 * content throws because its route hooks no longer have a valid match.
 */
class SlotErrorBoundary extends Component<
  PropsWithChildren,
  SlotErrorBoundaryState
> {
  state: SlotErrorBoundaryState = {
    hasError: false,
    prevChildren: null,
  };

  static getDerivedStateFromError(): Partial<SlotErrorBoundaryState> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: PropsWithChildren,
    state: SlotErrorBoundaryState,
  ): SlotErrorBoundaryState {
    if (state.hasError && props.children !== state.prevChildren) {
      return { hasError: false, prevChildren: props.children };
    }
    return { ...state, prevChildren: props.children };
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/**
 * We register a slot with a name that will be used to render the children of the SlotPortal component.
 */
export default memo(function Slot({ name, onRender }: SlotProps) {
  const manager = useContext(Slots);
  const [child, setSlotValue] = useState<ReactNode>(null);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const prevName = useRef(name);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const prevCallback = useRef(onRender);
    if (prevName.current !== name) {
      throw new Error('Slot name cannot change.');
    }
    if (prevCallback.current !== onRender) {
      throw new Error(
        'Slot onRender callback cannot change, please use useEvent or useCallback before sending the data.',
      );
    }
  }
  useEffect(() => {
    return manager.subscribe(name, (_, slotValue) => {
      onRender?.(slotValue);
      setSlotValue(slotValue);
    });
  }, [name, manager]);
  return <SlotErrorBoundary>{child}</SlotErrorBoundary>;
});
